import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { PageContainer, PageHeader } from "../components/PageHeader";
import AssetsPicker from "../components/AssetsPicker";
import { EditDrawer } from "../components/EditDrawer";
import {
  FieldControl,
  previewSlide,
  withPicked,
} from "../components/SignageSlideDialog";
import TemplateSlide from "../../tv/signage/TemplateSlide";
import { tokens } from "../theme";
import { describeError, useToast } from "../components/toast";
import { useFormat } from "../i18n/format";
import { foyerApi, type SignageScopeRef } from "../http/foyer";
import type {
  SignageTemplate,
  SignageTemplateField,
  SignageTemplateInstance,
  SignageTemplateRegion,
} from "@shared/types/signage-template";

/**
 * Update screens - the care unit's page.
 *
 * ## Who this is for
 *
 * The lowest signage tier, `canEditSignageContent`: a ward's own staff. The
 * content owner designed the screen and scheduled it; the ward changes today's
 * lunch, the activity list, who is on shift. So this page does ONE thing -
 * change what an existing slide says - and has no New, no Delete, no Rename and
 * no scheduling. The server enforces the same line (`signage_field_locked`);
 * this page just never offers what would be refused.
 *
 * ## Editing ON the slide, not in a form
 *
 * The slide is drawn full-width by the same renderer the wall runs, and only
 * the blocks the designer marked "unit staff can change this" are clickable -
 * outlined and labelled. Everything else is visibly locked. A form would ask a
 * nurse to map "Field 3" onto a picture of a screen in their head; this puts
 * the text box next to the words they can see.
 */

/** Which fields on this template a unit may change. */
export function unitFields(template: SignageTemplate): SignageTemplateField[] {
  const open = new Set(
    template.regions
      .filter((r) => r.binding.kind === "field" && r.binding.unitEditable)
      .map((r) => (r.binding as { field: string }).field)
  );
  return template.fields.filter((f) => open.has(f.key));
}

const PENCIL = "✎";

function fieldOf(region: SignageTemplateRegion): string | null {
  return region.binding.kind === "field" && region.binding.unitEditable
    ? region.binding.field
    : null;
}

export default function SignageUpdatesPage({
  premiseId,
}: {
  premiseId: string;
}) {
  const { t } = useTranslation(["signage", "common"]);
  const format = useFormat();
  const scope: SignageScopeRef = { kind: "premise", premiseId };
  const when = (iso: string | undefined): string => {
    if (!iso) return "";
    try {
      return format.dateTime(iso, { dateStyle: "short", timeStyle: "short" });
    } catch {
      return iso;
    }
  };
  const [templates, setTemplates] = useState<SignageTemplate[]>([]);
  const [slides, setSlides] = useState<SignageTemplateInstance[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    void Promise.all([
      foyerApi.listSignageTemplatesIn(scope),
      foyerApi.listSignageSlidesIn(scope),
    ])
      .then(([t, s]) => {
        if (!alive) return;
        setTemplates(t);
        setSlides(s);
      })
      .catch(() => {
        if (!alive) return;
        setTemplates([]);
        setSlides([]);
      })
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [premiseId, tick]);

  const byId = useMemo(
    () => new Map(templates.map((t) => [t.id, t] as const)),
    [templates]
  );

  // Only slides with something the unit can actually change. A slide that is
  // entirely locked is the content owner's; listing it here would be a row
  // that opens onto nothing.
  const editable = slides.filter((s) => {
    const t = byId.get(s.templateId);
    return t ? unitFields(t).length > 0 : false;
  });

  const open = editable.find((s) => s.id === openId) ?? null;
  const openTemplate = open ? byId.get(open.templateId) : undefined;

  return (
    <PageContainer>
      <PageHeader
        title={t("signageUpdatesPage.title")}
        subtitle={t("signageUpdatesPage.subtitle")}
      />

      {/*
       * Editing a slide opens a right-hand drawer OVER the card list, which
       * stays put underneath. The drawer is mounted only while a slide is
       * open; it animates in on mount, and its close plays the slide-out
       * before calling onClose, which is what unmounts it.
       */}
      {open && openTemplate && (
        <SlideUpdater
          key={open.id}
          scope={scope}
          slide={open}
          template={openTemplate}
          onClose={() => setOpenId(null)}
          onSaved={() => setTick((t) => t + 1)}
        />
      )}
      <Stack gap={1.5}>
        {loaded && editable.length === 0 && (
          <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
            {t("signageUpdatesPage.empty")}
          </Typography>
        )}
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          }}
        >
          {editable.map((s) => {
            const template = byId.get(s.templateId)!;
            return (
              <Box
                key={s.id}
                role="button"
                tabIndex={0}
                aria-label={s.name}
                data-testid={`update-slide-${s.id}`}
                onClick={() => setOpenId(s.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setOpenId(s.id);
                  }
                }}
                sx={{
                  border: `1px solid ${tokens.divider}`,
                  borderRadius: "10px",
                  overflow: "hidden",
                  cursor: "pointer",
                  "&:hover": { borderColor: tokens.brand },
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    aspectRatio: `${template.canvas.width} / ${template.canvas.height}`,
                    background: template.background || "#000",
                  }}
                >
                  <TemplateSlide
                    slide={previewSlide(template, s.values)}
                    timezone="Europe/Stockholm"
                  />
                </Box>
                <Box sx={{ p: 1.5 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "fontWeightSemibold" }}
                  >
                    {s.name}
                  </Typography>
                  <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                    {unitFields(template)
                      .map((f) => f.label)
                      .join(", ")}
                  </Typography>
                  {s.lastEditedBy && (
                    <Typography
                      variant="micro"
                      sx={{ color: tokens.inkMuted, mt: 0.5 }}
                    >
                      {t("signageUpdatesPage.lastChangedBy", {
                        name: s.lastEditedBy,
                        when: when(s.lastEditedAt),
                      })}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Stack>
    </PageContainer>
  );
}

/**
 * One slide, drawn large, with its unit-editable blocks clickable.
 *
 * The values sent back are the slide's WHOLE map with only unit fields
 * changed, because the PATCH replaces the map - and the server compares every
 * locked key against what is stored, so sending anything else would be
 * refused rather than silently overwrite the content owner's words.
 */
function SlideUpdater({
  scope,
  slide,
  template,
  onClose,
  onSaved,
}: {
  scope: SignageScopeRef;
  slide: SignageTemplateInstance;
  template: SignageTemplate;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const { t } = useTranslation(["signage", "common"]);
  const fields = unitFields(template);
  const [values, setValues] = useState<Record<string, string>>(slide.values);
  const [active, setActive] = useState<string>(fields[0]?.key ?? "");
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty = fields.some(
    (f) => (values[f.key] ?? "") !== (slide.values[f.key] ?? "")
  );
  const field = fields.find((f) => f.key === active) ?? null;
  const { width, height } = template.canvas;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await foyerApi.updateSignageSlideIn(scope, slide.id, { values });
      toast.success(t("signageUpdatesPage.updated", { name: slide.name }));
      onSaved();
    } catch (err) {
      setError(
        err instanceof Error && err.message.includes("locked")
          ? t("signageUpdatesPage.lockedError")
          : t("signageUpdatesPage.saveError")
      );
      toast.error(describeError(t, "updateSlide", err, { name: slide.name }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <EditDrawer
      open
      onClose={onClose}
      title={slide.name}
      // The slide is drawn large beside a 340px field column: the widest
      // drawer, so the words on it stay readable.
      width={1240}
      data-testid="update-drawer"
      footerTop={
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {error}
            </Alert>
          )}
        </>
      }
      // Save keeps the drawer open on purpose, as the page did before: the
      // toast is the confirmation, and a unit often changes a second field
      // right after the first. The X closes it.
      actions={
        <>
          <Button
            disabled={!dirty || saving}
            onClick={() => setValues(slide.values)}
          >
            {t("signageUpdatesPage.undoChanges")}
          </Button>
          <Button
            variant="contained"
            disabled={!dirty || saving}
            onClick={() => void save()}
            data-testid="update-save"
          >
            {t("common:buttons.save")}
          </Button>
        </>
      }
    >
      <Stack direction={{ xs: "column", md: "row" }} gap={3}>
        <Box
          data-testid="update-canvas"
          sx={{
            position: "relative",
            flex: 1,
            minWidth: 0,
            aspectRatio: `${width} / ${height}`,
            borderRadius: "8px",
            overflow: "hidden",
            background: template.background || "#000",
          }}
        >
          <Box sx={{ position: "absolute", inset: 0 }}>
            <TemplateSlide
              slide={previewSlide(template, values)}
              timezone="Europe/Stockholm"
            />
          </Box>
          {/* Locked parts are dimmed, so what CAN be changed stands out. */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.35)",
              pointerEvents: "none",
            }}
          />
          {template.regions.map((region) => {
            const key = fieldOf(region);
            if (!key) return null;
            const selected = key === active;
            return (
              <Box
                key={region.id}
                role="button"
                aria-label={t("signageUpdatesPage.changeField", {
                  label: fields.find((f) => f.key === key)?.label ?? key,
                })}
                data-testid={`update-region-${region.id}`}
                onClick={() => setActive(key)}
                sx={{
                  position: "absolute",
                  left: `${(region.rect.x / width) * 100}%`,
                  top: `${(region.rect.y / height) * 100}%`,
                  width: `${(region.rect.w / width) * 100}%`,
                  height: `${(region.rect.h / height) * 100}%`,
                  // Punches the dimming back out over an editable block.
                  boxShadow: selected
                    ? `0 0 0 3px ${tokens.brand}`
                    : `0 0 0 2px rgba(255,255,255,0.9)`,
                  backdropFilter: "brightness(1.55)",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 4,
                    left: 4,
                    typography: "micro",
                    color: "#fff",
                    background: tokens.brand,
                    px: 0.75,
                    borderRadius: "4px",
                  }}
                >
                  {PENCIL} {fields.find((f) => f.key === key)?.label ?? key}
                </Box>
              </Box>
            );
          })}
        </Box>

        <Stack gap={2} sx={{ width: { xs: "100%", md: 340 }, flexShrink: 0 }}>
          {field ? (
            <FieldControl
              key={field.key}
              field={field}
              value={values[field.key] ?? ""}
              onChange={(v) => {
                setValues((prev) => ({ ...prev, [field.key]: v }));
              }}
              onPick={() => setPicking(true)}
            />
          ) : (
            <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
              {t("signageUpdatesPage.clickHighlighted")}
            </Typography>
          )}
          {fields.length > 1 && (
            <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
              {t("signageUpdatesPage.youCanChange", {
                fields: fields.map((f) => f.label).join(", "),
              })}
            </Typography>
          )}
        </Stack>
      </Stack>

      <AssetsPicker
        open={picking}
        onClose={() => setPicking(false)}
        onSelect={(url) => {
          if (field) {
            setValues((prev) => ({
              ...prev,
              [field.key]: withPicked(field.type, prev[field.key] ?? "", url),
            }));
          }
          setPicking(false);
        }}
      />
    </EditDrawer>
  );
}
